import {
  Accordion,
  Badge,
  Button,
  Center,
  Group,
  Loader,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useState } from 'react';
import type { ReactNode } from 'react';
import { ErrorMessage } from '../components/ErrorMessage.js';
import { useT } from '../i18n/useT.js';
import { formatDate, formatMoney } from '../format.js';
import { useJournalEntries } from '../api/hooks.js';
import type { JournalEntry } from '../api/hooks.js';
import { JournalEntryForm } from './JournalEntryForm.js';

/**
 * The journal: what has been posted, and the form for posting more.
 *
 * Entries are shown newest first with their lines collapsed. The lines are what
 * make an entry auditable, so they are one click away rather than on a separate
 * page — checking a posting should not cost a navigation.
 */
export function JournalPage(): ReactNode {
  const t = useT();
  const [reference, setReference] = useState('');
  const [showForm, setShowForm] = useState(false);
  const entries = useJournalEntries(reference.trim() === '' ? {} : { reference: reference.trim() });

  return (
    <Stack gap="lg">
      <Group justify="space-between" wrap="wrap">
        <Title order={2}>{t('journal.title')}</Title>
        <Button
          onClick={() => {
            setShowForm((open) => !open);
          }}
        >
          {showForm ? t('journal.hideForm') : t('journal.newEntry')}
        </Button>
      </Group>

      {showForm && (
        <JournalEntryForm
          onPosted={() => {
            // Left open: postings usually come in batches, and closing the form
            // after each one would mean reopening it every time.
          }}
        />
      )}

      <TextInput
        label={t('journal.searchReference')}
        value={reference}
        onChange={(event) => {
          setReference(event.currentTarget.value);
        }}
        maw={320}
      />

      {entries.isPending && (
        <Center p="xl">
          <Loader />
        </Center>
      )}

      {entries.isError && <ErrorMessage error={entries.error} />}

      {entries.isSuccess && entries.data.items.length === 0 && (
        <Text c="dimmed">
          {reference.trim() === ''
            ? t('journal.empty')
            : t('journal.noMatch', { reference: reference.trim() })}
        </Text>
      )}

      {entries.isSuccess && entries.data.items.length > 0 && (
        <Accordion variant="separated">
          {entries.data.items.map((entry) => (
            <EntryRow key={entry.id} entry={entry} />
          ))}
        </Accordion>
      )}
    </Stack>
  );
}

function EntryRow({ entry }: { readonly entry: JournalEntry }): ReactNode {
  const t = useT();

  return (
    <Accordion.Item value={entry.id}>
      <Accordion.Control>
        <Group justify="space-between" wrap="nowrap" pr="md">
          <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
            <Text fw={600} style={{ whiteSpace: 'nowrap' }}>
              {formatDate(entry.date)}
            </Text>
            <Text lineClamp={1}>{entry.description}</Text>
            {entry.reference !== null && (
              <Badge variant="light" size="sm">
                {entry.reference}
              </Badge>
            )}
          </Group>
          <Text className="money-cell" fw={600} style={{ whiteSpace: 'nowrap' }}>
            {formatMoney(entry.totals.debit)}
          </Text>
        </Group>
      </Accordion.Control>

      <Accordion.Panel>
        <Table.ScrollContainer minWidth={520}>
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('journal.col.account')}</Table.Th>
                <Table.Th>{t('journal.col.text')}</Table.Th>
                <Table.Th style={{ textAlign: 'right' }}>{t('journal.col.debit')}</Table.Th>
                <Table.Th style={{ textAlign: 'right' }}>{t('journal.col.credit')}</Table.Th>
                <Table.Th style={{ textAlign: 'right' }}>{t('journal.col.vat')}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {entry.lines.map((line) => (
                <Table.Tr key={line.id}>
                  <Table.Td>
                    <Text size="sm">
                      {line.accountCode} — {line.accountName}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed">
                      {line.description ?? ''}
                    </Text>
                  </Table.Td>
                  {/*
                    A zero side is shown blank rather than as 0,00: every line
                    has one, and a column of zeros makes the column that matters
                    harder to scan.
                  */}
                  <Table.Td className="money-cell">
                    {line.debit === '0.0000' ? '' : formatMoney(line.debit)}
                  </Table.Td>
                  <Table.Td className="money-cell">
                    {line.credit === '0.0000' ? '' : formatMoney(line.credit)}
                  </Table.Td>
                  <Table.Td className="money-cell">
                    {line.vatCode === null ? '' : `${line.vatCode} ${formatMoney(line.vatAmount)}`}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Accordion.Panel>
    </Accordion.Item>
  );
}
